import { useQueryClient } from '@tanstack/react-query';

import { useToaster } from '@components/toaster';
import { useThrottledMutation as useMutation } from '@hooks/use-throttled-mutation';
import { useScopedTranslate } from '@locale/translate';

import {
  JourneyActionReminder,
  TravellerActionReminder,
  createJourneyActionReminder,
  createManualReminder,
  createTravellerActionReminder,
  deleteJourneyActionReminder,
  deleteTravellerActionReminder,
  updateJourneyActionReminder,
  updateTravellerActionReminder,
} from './requests';

export const useCreateJourneyActionReminder = () => {
  const { t } = useScopedTranslate(locale);
  const { showSuccess } = useToaster();
  return useMutation({
    mutationFn: createJourneyActionReminder,
    onSuccess: () => {
      showSuccess(t('reminderCreated'));
    },
  });
};

export const useUpdateJourneyActionReminder = () => {
  const { t } = useScopedTranslate(locale);
  const { showSuccess } = useToaster();
  return useMutation({
    mutationFn: (data: { shortId: string; reminder: JourneyActionReminder }) =>
      updateJourneyActionReminder(data.shortId, data.reminder),
    onSuccess: () => {
      showSuccess(t('reminderUpdated'));
    },
  });
};

export const useDeleteJourneyActionReminder = (shortId: string) => {
  const { t } = useScopedTranslate(locale);
  const { showSuccess } = useToaster();
  return useMutation({
    mutationFn: () => deleteJourneyActionReminder(shortId),
    onSuccess: () => {
      showSuccess(t('reminderDeleted'));
    },
  });
};

export const useCreateTravellerActionReminder = () => {
  const { t } = useScopedTranslate(locale);
  const queryClient = useQueryClient();
  const { showSuccess } = useToaster();
  return useMutation({
    mutationFn: createTravellerActionReminder,
    onSuccess: (data) => {
      queryClient.invalidateQueries(['traveller-action', data.travellerAction]);
      showSuccess(t('reminderCreated'));
    },
  });
};

export const useUpdateTravellerActionReminder = () => {
  const { t } = useScopedTranslate(locale);
  const queryClient = useQueryClient();
  const { showSuccess } = useToaster();
  return useMutation({
    mutationFn: (data: { shortId: string; reminder: TravellerActionReminder }) =>
      updateTravellerActionReminder(data.shortId, data.reminder),
    onSuccess: (data) => {
      queryClient.invalidateQueries(['traveller-action', data.travellerAction]);
      showSuccess(t('reminderUpdated'));
    },
  });
};

type DeleteTravellerReminder = {
  travellerAction: string;
  onSuccess?: () => void;
};

export const useDeleteTravellerActionReminder = (props: DeleteTravellerReminder) => {
  const { t } = useScopedTranslate(locale);
  const queryClient = useQueryClient();
  const { showSuccess } = useToaster();
  return useMutation({
    mutationFn: (shortId: string) => deleteTravellerActionReminder(shortId),
    onSuccess: () => {
      queryClient.refetchQueries(['traveller-action', props.travellerAction]);
      showSuccess(t('reminderDeleted'));
      props.onSuccess?.();
    },
  });
};

export const useCreateManualReminder = () => {
  const { t } = useScopedTranslate(locale);
  const { showSuccess } = useToaster();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createManualReminder,
    onSuccess: (data) => {
      queryClient.invalidateQueries(['traveller-action', data.travellerAction]);
      showSuccess(t('manualReminderCreated'));
    },
  });
};

const locale = {
  en: {
    reminderCreated: 'Reminder created',
    reminderUpdated: 'Reminder updated',
    reminderDeleted: 'Reminder deleted',
    manualReminderCreated: 'Manual reminder created',
  },
};
